import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Chip, ToggleButtonGroup, ToggleButton } from "@mui/material";
import { AreaChart, Area, ResponsiveContainer } from "recharts";
import { getConversations, getOverview } from "../api/adminApi";
import PublicIcon from "@mui/icons-material/Public";
import ForumIcon from "@mui/icons-material/Forum";
import InstagramIcon from "@mui/icons-material/Instagram";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

const STATUS_COLORS = { active: "#10b981", closed: "#6366f1", live_requested: "#f59e0b" };

const PLATFORMS = [
  { key: "", label: "All", icon: null },
  { key: "website", label: "Website", icon: <PublicIcon sx={{ fontSize: 16 }} /> },
  { key: "whatsapp", label: "WhatsApp", icon: <ForumIcon sx={{ fontSize: 16 }} /> },
  { key: "instagram", label: "Instagram", icon: <InstagramIcon sx={{ fontSize: 16 }} /> },
  { key: "ai_agent", label: "AI Agents", icon: <AutoAwesomeIcon sx={{ fontSize: 16 }} /> },
];

const Conversations = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [platform, setPlatform] = useState("");
  const [trendData, setTrendData] = useState([]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getConversations({ page, limit: 50, platform }),
      getOverview("7").catch(() => null),
    ]).then(([convData, ov]) => {
      setConversations(convData.conversations || []);
      setTotal(convData.total || 0);
      if (ov?.dailyConversations) {
        setTrendData(ov.dailyConversations.labels.map((l, i) => ({
          date: l.slice(5),
          conversations: ov.dailyConversations.values[i],
        })));
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [page, platform]);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 4 }}>
        <Box>
          <Typography fontWeight={800} fontSize={24} color="#1e293b">Conversations</Typography>
          <Typography variant="body2" color="#64748b" sx={{ mt: 0.5 }}>{total} total conversations</Typography>
        </Box>
        <ToggleButtonGroup value={platform} exclusive onChange={(e, v) => { setPlatform(v || ""); setPage(1); }} size="small">
          {PLATFORMS.map((p) => (
            <ToggleButton key={p.key} value={p.key}
              sx={{ textTransform: "none", fontWeight: 600, fontSize: 12, px: 1.5, color: platform === p.key ? "#6366f1" : "#64748b", borderColor: "#e2e8f0", "&.Mui-selected": { bgcolor: "#6366f112", color: "#6366f1" } }}>
              {p.icon && <Box sx={{ mr: 0.5, display: "flex", alignItems: "center" }}>{p.icon}</Box>}{p.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {trendData.length > 0 && (
        <Paper className="glass-card" sx={{ p: 2, mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
            <Box sx={{ minWidth: 100 }}>
              <Typography variant="caption" color="#64748b" fontWeight={600}>7-Day Trend</Typography>
              <Typography fontWeight={700} fontSize={20} color="#1e293b">{trendData.reduce((s, d) => s + d.conversations, 0)}</Typography>
              <Typography variant="caption" color="#64748b">total convs</Typography>
            </Box>
            <Box sx={{ flex: 1, height: 60 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData}>
                  <defs>
                    <linearGradient id="sparkColor" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area type="monotone" dataKey="conversations" stroke="#6366f1" strokeWidth={2} fill="url(#sparkColor)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </Paper>
      )}

      <Paper className="glass-card" sx={{ overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: "#64748b", fontWeight: 700, fontSize: 12, textTransform: "uppercase", borderColor: "#e2e8f0" }}>User</TableCell>
                <TableCell sx={{ color: "#64748b", fontWeight: 700, fontSize: 12, textTransform: "uppercase", borderColor: "#e2e8f0" }}>Bot</TableCell>
                <TableCell sx={{ color: "#64748b", fontWeight: 700, fontSize: 12, textTransform: "uppercase", borderColor: "#e2e8f0" }}>Status</TableCell>
                <TableCell sx={{ color: "#64748b", fontWeight: 700, fontSize: 12, textTransform: "uppercase", borderColor: "#e2e8f0" }}>Messages</TableCell>
                <TableCell sx={{ color: "#64748b", fontWeight: 700, fontSize: 12, textTransform: "uppercase", borderColor: "#e2e8f0" }}>Last Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, borderColor: "#e2e8f0" }}><CircularProgress /></TableCell></TableRow>
              ) : conversations.length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: "#64748b", borderColor: "#e2e8f0" }}>No conversations yet</TableCell></TableRow>
              ) : conversations.map((c) => (
                <TableRow key={c._id} hover sx={{ "&:hover": { bgcolor: "#f8faff" }, "&:last-child td": { border: 0 } }}>
                  <TableCell sx={{ borderColor: "#e2e8f0" }}>
                    <Typography fontWeight={600} fontSize={14} color="#1e293b">{c.userName || "Anonymous"}</Typography>
                    <Typography variant="caption" color="#64748b">ID: {c.sessionId?.slice(0, 12)}...</Typography>
                  </TableCell>
                  <TableCell sx={{ borderColor: "#e2e8f0" }}><Typography variant="body2" color="#475569">{c.botName || "Unknown"}</Typography></TableCell>
                  <TableCell sx={{ borderColor: "#e2e8f0" }}>
                    <Chip label={c.status?.replace("_", " ")} size="small"
                      sx={{ textTransform: "capitalize", fontWeight: 600, fontSize: 12, bgcolor: `${STATUS_COLORS[c.status] || "#64748b"}12`, color: STATUS_COLORS[c.status] || "#64748b", borderRadius: 1 }} />
                  </TableCell>
                  <TableCell sx={{ borderColor: "#e2e8f0" }}><Typography variant="body2" color="#475569">{c.messageCount}</Typography></TableCell>
                  <TableCell sx={{ borderColor: "#e2e8f0" }}><Typography variant="body2" color="#64748b">{new Date(c.updatedAt).toLocaleString()}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {total > 50 && (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 2 }}>
          <Chip label="Previous" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
            sx={{ color: "#1e293b", bgcolor: "#ffffff", border: "1px solid #e2e8f0", "&.Mui-disabled": { opacity: 0.3 } }} />
          <Chip label={`Page ${page} of ${Math.ceil(total / 50)}`} sx={{ color: "#64748b", bgcolor: "#ffffff", border: "1px solid #e2e8f0" }} />
          <Chip label="Next" disabled={page >= Math.ceil(total / 50)} onClick={() => setPage((p) => p + 1)}
            sx={{ color: "#1e293b", bgcolor: "#ffffff", border: "1px solid #e2e8f0", "&.Mui-disabled": { opacity: 0.3 } }} />
        </Box>
      )}
    </Box>
  );
};

export default Conversations;
